const REVEAL_CLASS_MAP = {
  up:         'animate-reveal-up',
  'up-short': 'animate-reveal-up-short',
  down:       'animate-reveal-down',
  'down-short': 'animate-reveal-down-short',
  left:       'animate-reveal-left',
  right:      'animate-reveal-right',
  fade:       'animate-reveal-fade',
  tile:       'animate-reveal-tile',
};

const mobileQuery = window.matchMedia('(max-width: 767px)');

function initScrollReveal() {
  const elements = document.querySelectorAll('[data-reveal]');
  if (!elements.length) return;

  const animating = new Set();

  const reset = (el) => {
    if (animating.has(el)) return;

    if (el._revealTimer) {
      clearTimeout(el._revealTimer);
      el._revealTimer = null;
    }

    if (el.dataset.reveal === 'letters') {
      el.querySelectorAll('span').forEach(span => {
        span.classList.remove('animate-reveal-letter');
        span.style.opacity = '0';
      });
    } else {
      const animClass = REVEAL_CLASS_MAP[el.dataset.reveal];
      if (animClass) el.classList.remove(animClass);
      el.style.opacity = '0';
    }
  };

  const reveal = (el) => {
    const direction = el.dataset.reveal;

    // Letter-by-letter
    if (direction === 'letters') {
      const stagger = parseInt(el.dataset.revealStagger, 10) || 40;
      const spans = el.querySelectorAll('span');
      animating.add(el);
      spans.forEach((span, i) => {
        setTimeout(() => {
          span.style.opacity = '';
          span.classList.add('animate-reveal-letter');
          if (i === spans.length - 1) {
            span.addEventListener('animationend', () => {
              animating.delete(el);
              const r = el.getBoundingClientRect();
              if (r.bottom < 0 || r.top > window.innerHeight) {
                el.querySelectorAll('span').forEach(s => {
                  s.classList.remove('animate-reveal-letter');
                  s.style.opacity = '0';
                });
              }
            }, { once: true });
          }
        }, i * stagger);
      });
      return;
    }

    // Directional
    const animClass = REVEAL_CLASS_MAP[direction];
    const delay = parseInt(el.dataset.revealDelay, 10) || 0;

    if (!animClass) return;
    if (el.classList.contains(animClass)) return;

    const apply = () => {
      el._revealTimer = null;
      animating.add(el);
      el.style.opacity = '';
      el.classList.add(animClass);
      el.addEventListener('animationend', () => {
        animating.delete(el);
        // Scrolled out mid-animation: reset now
        const r = el.getBoundingClientRect();
        if (r.bottom < 0 || r.top > window.innerHeight) {
          el.classList.remove(animClass);
          el.style.opacity = '0';
        }
      }, { once: true });
    };

    if (delay > 0) {
      el._revealTimer = setTimeout(apply, delay);
    } else {
      apply();
    }
  };

  const handleReveal = (entries, obs) => {
    entries.forEach((entry) => {
      if (!entry.isIntersecting) return;
      reveal(entry.target);
      if (entry.target.dataset.revealOnce === 'true') {
        obs.unobserve(entry.target);
        resetObserver.unobserve(entry.target);
      }
    });
  };

  const observer = new IntersectionObserver(handleReveal, {
    rootMargin: '0px 0px -150px 0px',
    threshold: 0.1,
  });

  // Above the fold at load: no bottom margin, or it waits for a scroll
  const initialObserver = new IntersectionObserver(handleReveal, {
    rootMargin: '0px',
    threshold: 0.1,
  });

  // Reset only when fully off screen, never inside the reveal margin
  const resetObserver = new IntersectionObserver((entries) => {
    entries.forEach((entry) => {
      if (!entry.isIntersecting) reset(entry.target);
    });
  }, { rootMargin: '0px', threshold: 0 });

  elements.forEach((el) => {
    // data-reveal-no-mobile: skip on mobile
    if ('revealNoMobile' in el.dataset && mobileQuery.matches) return;

    if (el.dataset.reveal === 'letters') {
      el.querySelectorAll('span').forEach(span => span.style.opacity = '0');
    } else {
      el.style.opacity = '0';
    }

    const rect = el.getBoundingClientRect();
    const inViewOnLoad = rect.top < window.innerHeight && rect.bottom > 0;
    (inViewOnLoad ? initialObserver : observer).observe(el);
    resetObserver.observe(el);
  });
}

// Scan image wipe — fires after the reveal animation on the same element ends
function initScanAnimate() {
  const els = document.querySelectorAll('[data-scan-animate]');
  if (!els.length) return;

  els.forEach((el) => {
    // Re-trigger each time the reveal animation completes (not child scan animations)
    el.addEventListener('animationend', (e) => {
      if (e.target === el && e.animationName === 'revealFromLeft') {
        el.classList.add('is-visible');
      }
    });

    // Remove is-visible when out of view so the scan wipe replays on re-entry
    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) {
          el.classList.remove('is-visible');
        }
      });
    }, { rootMargin: '0px 0px -150px 0px', threshold: 0.1 });

    observer.observe(el);
  });
}

function initCarouselReveal() {
  const carousels = document.querySelectorAll('[data-reveal-carousel]');
  if (!carousels.length) return;

  carousels.forEach((wrapper) => {
    let isAnimating = false;
    let slideTimers = [];

    const getSlides = () => [...wrapper.querySelectorAll('swiper-slide:not(.swiper-slide-duplicate) > *:first-child')];
    getSlides().forEach((s) => s.style.opacity = '0');

    const cancelTimers = () => {
      slideTimers.forEach(clearTimeout);
      slideTimers = [];
    };

    const resetSlides = () => {
      getSlides().forEach((s) => {
        s.classList.remove('animate-reveal-up-short');
        s.style.opacity = '0';
      });
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          if (isAnimating) return;

          const slides = getSlides().filter((s) => {
            const r = s.getBoundingClientRect();
            return r.right > 0 && r.left < window.innerWidth;
          });
          slides.sort((a, b) => a.getBoundingClientRect().left - b.getBoundingClientRect().left);

          if (!slides.length) return;

          isAnimating = true;
          slides.forEach((slide, i) => {
            const t = setTimeout(() => {
              slide.style.opacity = '';
              slide.classList.add('animate-reveal-up-short');
              if (i === slides.length - 1) {
                slide.addEventListener('animationend', () => {
                  getSlides().forEach((s) => s.style.opacity = '');
                  isAnimating = false;
                  // If scrolled out before animation finished, reset immediately
                  const r = wrapper.getBoundingClientRect();
                  if (r.bottom < 0 || r.top > window.innerHeight) {
                    resetSlides();
                  }
                }, { once: true });
              }
            }, i * 100);
            slideTimers.push(t);
          });
        } else {
          // Cancel pending timers before checking isAnimating
          cancelTimers();
          if (isAnimating) {
            isAnimating = false;
          }
          resetSlides();
        }
      });
    }, { rootMargin: '0px 0px -100px 0px', threshold: 0.05 });

    observer.observe(wrapper);
  });
}

function initGraphTiles() {
  const CIRC = 2 * Math.PI * 30;

  function animateTile(tile) {
    tile.querySelectorAll('.graph-ring').forEach((ring, i) => {
      const pct = parseFloat(ring.dataset.percent) || 0;
      const target = CIRC * (1 - pct / 100);
      ring.style.transition = 'none';
      ring.style.strokeDashoffset = CIRC;
      setTimeout(() => {
        ring.style.transition = 'stroke-dashoffset 1s cubic-bezier(0.16, 1, 0.3, 1)';
        ring.style.strokeDashoffset = target;
      }, i * 250 + 50);
    });
  }

  function resetTile(tile) {
    tile.querySelectorAll('.graph-ring').forEach((ring) => {
      ring.style.transition = 'none';
      ring.style.strokeDashoffset = CIRC;
    });
  }

  const tiles = document.querySelectorAll('[data-graph-tile]');
  if (!tiles.length) return;

  const observer = new IntersectionObserver((entries) => {
    entries.forEach((entry) => {
      if (entry.isIntersecting) {
        setTimeout(() => animateTile(entry.target), 700);
      } else {
        resetTile(entry.target);
      }
    });
  }, { threshold: 0.3, rootMargin: '0px 0px -100px 0px' });

  tiles.forEach((el) => observer.observe(el));
}

function initCurtainReveal() {
  const grids = document.querySelectorAll('[data-curtain-grid]');
  if (!grids.length) return;

  grids.forEach((grid) => {
    const tiles = [...grid.children];

    tiles.forEach((tile) => {
      const curtain = document.createElement('div');
      curtain.style.cssText = 'position:absolute;inset:0;background:#051C2C;z-index:20;transform:translateY(0);pointer-events:none;';
      tile.appendChild(curtain);
    });

    let played = false;
    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting && !played) {
          played = true;
          observer.disconnect();
          tiles.forEach((tile, i) => {
            const curtain = tile.lastElementChild;
            setTimeout(() => {
              curtain.style.transition = 'transform 0.65s cubic-bezier(0.77, 0, 0.175, 1)';
              curtain.style.transform = 'translateY(-101%)';
            }, i * 80 + 150);
          });
        }
      });
    }, { threshold: 0.1, rootMargin: '0px 0px -80px 0px' });

    observer.observe(grid);
  });
}

function initStatsChart() {
  const charts = document.querySelectorAll('[data-stats-chart]');
  if (!charts.length) return;

  charts.forEach((chart) => {
    const t1 = chart.querySelector('#chart-t1');
    const t2 = chart.querySelector('#chart-t2');
    const t3 = chart.querySelector('#chart-t3');
    const fill = chart.querySelector('#chart-fill');
    const endpointLabels = chart.querySelector('#chart-endpoint-labels');
    const textBig = chart.querySelector('#chart-text-big');
    const circle = chart.querySelector('#chart-circle');
    const circleText = chart.querySelector('#chart-circle-text');

    [t1, t3].forEach((path) => {
      if (!path) return;
      const len = path.getTotalLength();
      path.style.strokeDasharray = len;
      path.style.strokeDashoffset = len;
    });

    if (t2) {
      const svg = t2.closest('svg');
      let defs = svg.querySelector('defs');
      if (!defs) {
        defs = document.createElementNS('http://www.w3.org/2000/svg', 'defs');
        svg.insertBefore(defs, svg.firstChild);
      }
      const clipPath = document.createElementNS('http://www.w3.org/2000/svg', 'clipPath');
      clipPath.setAttribute('id', 'chart-t2-clip');
      const clipRect = document.createElementNS('http://www.w3.org/2000/svg', 'rect');
      clipRect.setAttribute('x', '0');
      clipRect.setAttribute('y', '0');
      clipRect.setAttribute('width', '0');
      clipRect.setAttribute('height', '290');
      clipPath.appendChild(clipRect);
      defs.appendChild(clipPath);
      t2.setAttribute('clip-path', 'url(#chart-t2-clip)');
      t2._clipRect = clipRect;
    }
    [fill, endpointLabels, textBig, circleText].forEach((el) => {
      if (el) el.style.opacity = '0';
    });

    if (circle) {
      const circ = 2 * Math.PI * parseFloat(circle.getAttribute('r'));
      circle.style.strokeDasharray = circ;
      circle.style.strokeDashoffset = circ;
    }

    let played = false;
    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting || played) return;
        played = true;
        observer.disconnect();

        if (t1) setTimeout(() => {
          t1.style.transition = 'stroke-dashoffset 2s cubic-bezier(0.16, 1, 0.3, 1)';
          t1.style.strokeDashoffset = '0';
        }, 500);

        if (t2) setTimeout(() => {
          const dur = 1800;
          const start = performance.now();
          (function step(now) {
            const p = Math.min((now - start) / dur, 1);
            const eased = 1 - Math.pow(1 - p, 3);
            t2._clipRect.setAttribute('width', 613 * eased);
            if (p < 1) requestAnimationFrame(step);
          })(performance.now());
        }, 500);

        if (t3) setTimeout(() => {
          t3.style.transition = 'stroke-dashoffset 2.4s cubic-bezier(0.16, 1, 0.3, 1)';
          t3.style.strokeDashoffset = '0';
        }, 900);

        if (fill) setTimeout(() => {
          fill.style.transition = 'opacity 1.2s ease';
          fill.style.opacity = '1';
        }, 900);

        if (endpointLabels) setTimeout(() => {
          endpointLabels.style.transition = 'opacity 0.6s ease';
          endpointLabels.style.opacity = '1';
        }, 2300);

        if (textBig) setTimeout(() => {
          textBig.style.transition = 'opacity 0.6s ease';
          textBig.style.opacity = '1';
        }, 2800);

        if (circle) setTimeout(() => {
          circle.style.transition = 'stroke-dashoffset 0.6s cubic-bezier(0.16, 1, 0.3, 1)';
          circle.style.strokeDashoffset = '0';
          circle.style.opacity = '1';
        }, 2400);

        if (circleText) setTimeout(() => {
          circleText.style.transition = 'opacity 0.3s ease';
          circleText.style.opacity = '1';
        }, 2400);
      });
    }, { threshold: 0.3, rootMargin: '0px 0px -80px 0px' });

    observer.observe(chart);
  });
}

function initStatBars() {
  const sections = document.querySelectorAll('[data-stat-bars]');
  if (!sections.length) return;

  sections.forEach((section) => {
    let played = false;
    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting && !played) {
          played = true;
          observer.disconnect();
          const slides = [...section.querySelectorAll('swiper-slide:not(.swiper-slide-duplicate)')];
          let delay = 200;
          slides.forEach((slide) => {
            slide.querySelectorAll('[data-stat-bar]').forEach((bar) => {
              const target = bar.dataset.statBar;
              setTimeout(() => {
                bar.style.transition = 'width 0.9s cubic-bezier(0.16, 1, 0.3, 1)';
                bar.style.width = target + '%';
              }, delay);
              delay += 80;
            });
          });
        }
      });
    }, { threshold: 0.2, rootMargin: '0px 0px -80px 0px' });

    observer.observe(section);
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    initScrollReveal();
    initScanAnimate();
    initGraphTiles();
    initCarouselReveal();
    initCurtainReveal();
    initStatBars();
    initStatsChart();
  });
} else {
  initScrollReveal();
  initScanAnimate();
  initGraphTiles();
  initCarouselReveal();
  initCurtainReveal();
  initStatBars();
  initStatsChart();
}
